import React, { useState } from "react";
import {
  Button,
  Menu,
  MenuItem,
  Checkbox,
  ListItemText,
  Popover,
  Box,
  Chip,
} from "@mui/material";
import { getYears } from "../../../../../components/CommonFunctions/CommonFunctions";
import moment from "moment";

const years = getYears(moment().year() - 4, moment().year());
const months: any = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export default function NestedYearMonthMenu({
  setHoveredYear,
  hoveredYear,
  selectedMonths,
  setSelectedMonths,
}: any) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const [subAnchorEl, setSubAnchorEl] = useState<null | HTMLElement>(null);

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setHoveredYear(null);
    setSubAnchorEl(null);
  };

  const handleYearHover = (
    event: React.MouseEvent<HTMLElement>,
    year: number
  ) => {
    setHoveredYear(year);
    setSubAnchorEl(event.currentTarget);
  };

  const handleMonthToggle = (year: number, month: string) => {
    setSelectedMonths((prev: any) => {
      const current = prev[year] || [];
      const exists = current.includes(month);
      return {
        ...prev,
        [year]: exists
          ? current.filter((m: any) => m !== month)
          : [...current, month],
      };
    });
  };

  const open = Boolean(anchorEl);
  const subOpen = Boolean(subAnchorEl) && hoveredYear !== null;

  const handleRemoveChip = (year: number, month: string) => {
    handleMonthToggle(year, month);
  };

  const isEmpty = Object.values(selectedMonths).every(
    (months) => !Array.isArray(months) || months.length === 0
  );

  return (
    <Box
      sx={{
        marginTop: "22px",
        display: "flex",
        alignItems: "center",
        gap: "20px",
      }}
    >
      <Button variant="outlined" onClick={handleOpen}>
        Select Year & Month
      </Button>

      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        {years.map((year) => (
          <MenuItem
            key={year}
            onMouseEnter={(e) => handleYearHover(e, year)}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              backgroundColor:
                hoveredYear === year
                  ? "rgba(17, 200, 155, 0.1)"
                  : "transparent",
              color: hoveredYear === year ? "#11C89B" : "inherit",
              transition: "background-color 0.2s ease",
            }}
          >
            {year}
            <span style={{ opacity: 0.4 }}>▶</span>
          </MenuItem>
        ))}
      </Menu>

      <Popover
        anchorEl={subAnchorEl}
        open={subOpen}
        onClose={() => {
          setSubAnchorEl(null);
        }}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        disableRestoreFocus
      >
        <Box sx={{ p: 1, minWidth: 160, maxHeight: 250, overflow: "scroll" }}>
          {hoveredYear &&
            months?.map((month: any) => (
              <MenuItem
                key={month}
                onClick={() => handleMonthToggle(hoveredYear, month)}
              >
                <Checkbox
                  checked={
                    selectedMonths[hoveredYear]?.includes(month) || false
                  }
                />
                <ListItemText primary={month} />
              </MenuItem>
            ))}
        </Box>
      </Popover>

      <Box
        sx={{
          mt: 2,
          minHeight: 100,
          maxWidth: 400,
          maxHeight: 100,
          minWidth: 100,
          overflowY: "auto",
          overflowX: "hidden",
          p: 1,
          border: "1px solid #e0e0e0",
          borderRadius: "8px",
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          backgroundColor: "#fafafa",
          scrollbarWidth: "thin",
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#c1c1c1",
            borderRadius: "4px",
          },
        }}
      >
        {isEmpty ? (
          <Box
            sx={{
              color: "text.secondary",
              fontSize: 14,
              textAlign: "center",
              alignSelf: "center",
              width: "100%",
            }}
          >
            No selections
          </Box>
        ) : (
          Object.entries(selectedMonths).map(([year, months]: any) =>
            months.map((month: string) => (
              <Chip
                key={`${year}-${month}`}
                label={`${month} ${year}`}
                onDelete={() => handleRemoveChip(Number(year), month)}
                size="small"
                color="primary"
                variant="outlined"
              />
            ))
          )
        )}
      </Box>
    </Box>
  );
}
